import { z } from 'zod';

export const USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,64}$/;

export const operatorRoleSchema = z.enum(['admin', 'developer_operator', 'manufacturer_operator']);

/** Public operator DTO from GET /api/users — never includes passwordHash. */
export const operatorSchema = z.object({
  id: z.string(),
  username: z.string(),
  email: z.string().nullable(),
  role: operatorRoleSchema,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastLoginAt: z.string().nullable(),
});

/** GET /api/users returns a plain array (no pagination server-side). */
export const operatorListSchema = z.array(operatorSchema);

export const createOperatorSchema = z.object({
  username: z.string().regex(USERNAME_PATTERN, 'errors.validation.usernameInvalid'),
  password: z.string().min(8, 'errors.validation.passwordTooShort'),
  role: operatorRoleSchema,
});

/** Invite mode: no password — the operator sets their own via a one-time email link. */
export const inviteOperatorSchema = z.object({
  username: z.string().regex(USERNAME_PATTERN, 'errors.validation.usernameInvalid'),
  email: z.string().email('errors.validation.emailInvalid'),
  role: operatorRoleSchema,
});

export const resetPasswordSchema = z.object({
  password: z.string().min(8, 'errors.validation.passwordTooShort'),
});

/** POST /api/users may add an `invite` block (invite mode / resend-invite). */
export const createOperatorResponseSchema = operatorSchema.extend({
  invite: z
    .object({
      delivered: z.boolean(),
      reason: z.string().optional(),
      // Present only when the email was NOT sent (SMTP degrade) — manual handover.
      inviteUrl: z.string().optional(),
    })
    .optional(),
});

export type Operator = z.infer<typeof operatorSchema>;
export type OperatorRole = z.infer<typeof operatorRoleSchema>;
export type CreateOperator = z.infer<typeof createOperatorSchema>;
export type InviteOperator = z.infer<typeof inviteOperatorSchema>;
export type CreateOperatorResponse = z.infer<typeof createOperatorResponseSchema>;

/** Operator invited by email who has not set a password yet. */
export function isPendingInvite(user: Operator): boolean {
  return !user.active && user.email != null && user.lastLoginAt == null;
}
